import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './Dialog';
import { DialogErrorAlert, useDialogError } from './DialogError';
import Button from './Button';

interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'destructive' | 'warning' | 'default';
  onConfirm: () => void | Promise<void>;
  isLoading?: boolean;
  confirmClassName?: string;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  isLoading = false,
  confirmClassName,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = React.useState(false);
  const dialogError = useDialogError();
  const buttonVariant = variant === 'destructive' ? 'destructive' : 'default';
  const busy = isLoading || submitting;

  const { clear } = dialogError;
  React.useEffect(() => {
    if (open) {
      setSubmitting(false);
      clear();
    }
  }, [open, clear]);

  const handleConfirm = async () => {
    if (busy) return;
    clear();
    setSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // A rejected confirm keeps the dialog open and says why, so the user can retry or cancel.
      dialogError.capture(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="sm"
        hideCloseButton
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogErrorAlert error={dialogError.error} />
          <DialogDescription>{description}</DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {cancelLabel ?? t('Cancel')}
          </Button>
          <Button
            variant={buttonVariant}
            className={confirmClassName}
            onClick={handleConfirm}
            loading={busy}
            disabled={busy}
          >
            {confirmLabel ?? t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlertDialog;
